/**
 * FolderSidebar — folder list for report organization.
 *
 * Extracted from Reports components where it was duplicated in 3 of 4 sections.
 */
import { FolderOpen } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { ReportFolder } from '@/lib/reports.types';

interface FolderSidebarProps {
  folders: ReportFolder[];
  activeFolderId: string | null;
  onFolderChange: (folderId: string | null) => void;
  /** Total reports count per folder — key is folderId, null key for unfiled */
  reportCounts: Record<string, number>;
  /** Active color theme (default: 'brand') */
  accentColor?: 'brand' | 'sky' | 'emerald';
}

const ACCENT_ACTIVE: Record<string, string> = {
  brand: 'bg-brand-50 text-brand-700 font-medium',
  sky: 'bg-sky-50 text-sky-700 font-medium',
  emerald: 'bg-emerald-50 text-emerald-700 font-medium',
};

const ACCENT_COUNT: Record<string, string> = {
  brand: 'text-brand-500',
  sky: 'text-sky-500',
  emerald: 'text-emerald-500',
};

export function FolderSidebar({
  folders,
  activeFolderId,
  onFolderChange,
  reportCounts,
  accentColor = 'brand',
}: FolderSidebarProps) {
  if (folders.length === 0) return null;

  return (
    <div className="w-48 shrink-0">
      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-2">
        Folders
      </h3>
      <div className="space-y-0.5">
        <FolderItem
          label="All Reports"
          active={activeFolderId === null}
          onClick={() => onFolderChange(null)}
          count={reportCounts['_unfiled'] ?? 0}
          accentColor={accentColor}
        />
        {folders.map((folder) => (
          <FolderItem
            key={folder.id}
            label={folder.name}
            active={activeFolderId === folder.id}
            onClick={() => onFolderChange(folder.id)}
            count={reportCounts[folder.id] ?? 0}
            accentColor={accentColor}
          />
        ))}
      </div>
    </div>
  );
}

function FolderItem({
  label,
  active,
  onClick,
  count,
  accentColor = 'brand',
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  count: number;
  accentColor?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-lg text-sm transition-colors duration-150',
        active
          ? ACCENT_ACTIVE[accentColor] ?? ACCENT_ACTIVE.brand
          : 'text-slate-600 hover:bg-slate-100',
      )}
    >
      <FolderOpen className="w-4 h-4 shrink-0" />
      <span className="truncate flex-1 text-left">{label}</span>
      <span
        className={cn(
          'text-xs tabular-nums',
          active ? ACCENT_COUNT[accentColor] ?? ACCENT_COUNT.brand : 'text-slate-400',
        )}
      >
        {count}
      </span>
    </button>
  );
}
